import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { Card } from '../components/ui/Card';
import { Table } from '../components/ui/Table';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { SearchInput } from '../components/ui/SearchInput';
import { Pagination } from '../components/ui/Pagination';
import { CountBand } from '../components/ui/CountBand';
import { apiRequestGet, apiList, apiPost, apiUpdate } from '../api/apiClient';
import { formatDate } from '../utils/formatters';
import { AuthContext } from '../context/AuthContext';
import { isAdmin, isDealer, isCustomer, isPropertyManager } from '../utils/roles';
import { Role } from '../types';
import { Users, UserCheck, UserX, Clock, RefreshCw } from 'lucide-react';
import { refreshAllCounters } from '../utils/refreshCounters';
import { FilterPills } from '../components/ui/FilterPills';
import { deviceAccessId, eligibleUnlockDevices } from '../utils/deviceCapabilities';
import { formatDateTimeWithZone, localDateTimeInputToIso, localTimeZone, toLocalDateTimeInput } from '../utils/dateTime';
import { effectiveAccessIds, modeFromScope } from '../utils/visitorPassAccess';
import { buildVisitorPassPayload } from '../utils/visitorPassPayload';

type StatusFilter = 'all' | 'active' | 'expired' | 'revoked';

const PAGE_SIZE = 20;

function getGuestStatus(guest: any): 'active' | 'expired' | 'revoked' {
  if (guest.revoked || guest.is_revoked) return 'revoked';
  if (guest.expires_at) {
    const expiry = new Date(guest.expires_at);
    if (expiry < new Date()) return 'expired';
  }
  return 'active';
}

function parseDoorAccess(doorAccessJson: any): string[] {
  if (!doorAccessJson) return [];
  try {
    if (typeof doorAccessJson === 'string') {
      const parsed = JSON.parse(doorAccessJson);
      return Array.isArray(parsed) ? parsed : [];
    }
    if (Array.isArray(doorAccessJson)) return doorAccessJson;
  } catch {}
  return [];
}

function formatDoorAccess(doorAccessJson: any, propertyDevices: any[]): string {
  const doors = parseDoorAccess(doorAccessJson);
  if (doors.includes('*')) return 'All eligible doors';
  if (!doors.length) return 'No door access';
  if (propertyDevices.length === 0) return `${doors.length} door${doors.length !== 1 ? 's' : ''}`;
  return `${doors.length} / ${propertyDevices.length}`;
}

export const Guests: React.FC = () => {
  const { user } = useContext(AuthContext);
  const role = (user?.role || 'customer') as Role;

  const [guests, setGuests] = useState<any[]>([]);
  const [properties, setProperties] = useState<Record<string, any>>({});
  const [tenants, setTenants] = useState<Record<number, any>>({});
  const [units, setUnits] = useState<any[]>([]);
  const [devices, setDevices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadWarning, setLoadWarning] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [page, setPage] = useState(1);

  // Modal state
  const [selectedGuest, setSelectedGuest] = useState<any | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDoorAccess, setModalDoorAccess] = useState<string[]>([]);
  const [savingDoors, setSavingDoors] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState<number | null>(null);
  const [pinRevealed, setPinRevealed] = useState(false);
  const [auditEvents, setAuditEvents] = useState<any[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [editingGuest, setEditingGuest] = useState(false);
  const [guestEditForm, setGuestEditForm] = useState<any>({});
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const emptyGuest = { property_id: '', unit_id: '', name: '', phone: '', email: '', starts_at: '', expires_at: '', access_mode: 'all' as 'all' | 'selected' | 'none', device_ids: [] as string[], consent_enabled: false, consent_title: 'Visitor consent', consent_text: '', consent_acknowledgement: 'I have read and agree to this consent form.' };
  const [newGuest, setNewGuest] = useState(emptyGuest);
  const [createdPass, setCreatedPass] = useState<any | null>(null);
  const [revealedPins, setRevealedPins] = useState<Record<string, string>>({});
  const [revealingPinId, setRevealingPinId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadWarning('');
    try {
      const failedSources: string[] = [];
      const partial = <T,>(label: string, work: Promise<T>, fallback: T): Promise<T> =>
        work.catch((error) => { console.error(`Guests ${label} failed:`, error); failedSources.push(label); return fallback; });
      const [legacyGuestData, visitorPasses, propertyData, tenantData, unitData, deviceData] = await Promise.all([
        partial('legacy guest codes', apiList('guest_pins'), []),
        partial('secure visitor passes', apiRequestGet('/visitors/passes'), []),
        partial('properties', apiList('properties'), []),
        partial('people', apiList('tenants'), []),
        partial('units', apiList('units'), []),
        partial('devices', apiList('devices'), []),
      ]);
      if (failedSources.length) setLoadWarning(`Some guest data could not be loaded (${failedSources.join(', ')}). The list may be incomplete. Refresh or sign in again.`);

      // Visitor Passes are canonical PostgreSQL guest records. Load them
      // directly while retaining legacy guest PINs until migration completes.
      const passGuests = (Array.isArray(visitorPasses) ? visitorPasses : []).map((p: any) => ({
        ...p,
        _source: 'visitor_pass',
        pin_type: p.pass_type === 'delivery' ? 'delivery' : 'guest',
        guest_name: p.visitor_name,
        guest_phone: p.phone_last4 ? `••• ••• ${p.phone_last4}` : '—',
        expires_at: p.valid_until,
        created_at: p.valid_from,
        door_access_json: p.access_scope,
        granted_by_tenant_id: p.authorized_by,
        revoked: p.status === 'revoked',
        is_revoked: p.status === 'revoked',
      }));
      const guestData = [...passGuests, ...(legacyGuestData || [])];

      const propMap: Record<string, any> = {};
      (propertyData || []).forEach((p: any) => { propMap[String(p.id)] = p; });
      setProperties(propMap);

      const tenantMap: Record<number, any> = {};
      (tenantData || []).forEach((t: any) => { tenantMap[t.id] = t; });
      setTenants(tenantMap);
      setUnits(unitData || []);

      setDevices(deviceData || []);

      // Apply role-based filtering to guests
      let filteredGuests = (guestData || []).filter((g: any) =>
        String(g?.pin_type ?? g?.kind ?? 'guest').toLowerCase() !== 'delivery'
      );

      if (isAdmin(role)) {
        // Super admin / admin: sees all guests — no filter
      } else if (isDealer(role)) {
        // Dealer/integrator: sees guests for their customers' properties
        const dealerPropertyIds = new Set(
          (propertyData || [])
            .filter((p: any) => p.integrator_id === user?.integrator_id || p.dealer_id === user?.integrator_id)
            .map((p: any) => String(p.id))
        );
        filteredGuests = filteredGuests.filter((g: any) => dealerPropertyIds.has(String(g.property_id)));
      } else if (isCustomer(role)) {
        // Customer: sees guests for their properties
        const customerPropertyIds = new Set(
          (propertyData || [])
            .filter((p: any) => p.customer_id === user?.customer_id)
            .map((p: any) => String(p.id))
        );
        filteredGuests = filteredGuests.filter((g: any) => customerPropertyIds.has(String(g.property_id)));
      } else if (isPropertyManager(role)) {
        // Property manager: sees guests for their assigned properties
        const pmPropertyIds = new Set(
          (user?.property_ids || (user?.property_id ? [user.property_id] : [])).map(String)
        );
        filteredGuests = filteredGuests.filter((g: any) => pmPropertyIds.has(String(g.property_id)));
      }

      setGuests(filteredGuests);
    } catch (e) {
      console.error('Failed to load guests:', e);
      setLoadWarning('Guests could not be loaded. Refresh or sign in again.');
    } finally {
      setLoading(false);
    }
  }, [role, user]);

  useEffect(() => { load(); }, [load]);

  // Enrich guests with computed fields
  const enrichedGuests = useMemo(() => {
    return guests.map((g: any) => {
      const property = properties[String(g.property_id)] || {};
      const grantedByTenant = tenants[g.granted_by_tenant_id] || {};
      // Show unit from guest's unit_id, fall back to granting tenant's unit, or "Building" guest
      const unit = g.unit_number || (g.unit_id
        ? (grantedByTenant.unit_number || g.unit_id.slice(0, 8))
        : (grantedByTenant.unit_number || grantedByTenant.unit || 'Building'));
      const status = getGuestStatus(g);

      return {
        ...g,
        _property_name: property.name || `Property #${g.property_id || '?'}`,
        _granted_by_name: g.granted_by_name || [grantedByTenant.first_name, grantedByTenant.last_name].filter(Boolean).join(' ') || 'Property Manager',
        _unit: unit || '—',
        _status: status,
        _guest_name: [g.guest_first_name || g.first_name, g.guest_last_name || g.last_name].filter(Boolean).join(' ') || g.guest_name || g.label || '—',
        _phone: g.guest_phone || g.phone || g.phone_number || '—',
        _email: g.guest_email || g.email || '—',
      };
    });
  }, [guests, properties, tenants]);

  // Summary counts
  const totalGuests = enrichedGuests.length;
  const activeCount = enrichedGuests.filter(g => g._status === 'active').length;
  const expiredCount = enrichedGuests.filter(g => g._status === 'expired').length;
  const revokedCount = enrichedGuests.filter(g => g._status === 'revoked').length;

  // Filtered guests
  const filtered = useMemo(() => {
    let result = enrichedGuests;

    if (statusFilter !== 'all') {
      result = result.filter(g => g._status === statusFilter);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(g =>
        g._guest_name.toLowerCase().includes(q) ||
        g._phone.toLowerCase().includes(q) ||
        g._email.toLowerCase().includes(q) ||
        g._property_name.toLowerCase().includes(q) ||
        g._granted_by_name.toLowerCase().includes(q)
      );
    }

    return result;
  }, [enrichedGuests, statusFilter, search]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const startIndex = (page - 1) * PAGE_SIZE;
  const endIndex = Math.min(startIndex + PAGE_SIZE, filtered.length);
  const pageData = filtered.slice(startIndex, endIndex);

  // Pin copy
  const handleCopyPin = (guestId: number, pin: string) => {
    navigator.clipboard.writeText(pin).catch(() => {});
    setCopyFeedback(guestId);
    setTimeout(() => setCopyFeedback(null), 1500);
  };

  // Open modal
  const openGuestModal = async (guest: any) => {
    setSelectedGuest(guest);
    setModalDoorAccess(parseDoorAccess(guest.door_access_json));
    setPinRevealed(false);
    setEditingGuest(false);
    setGuestEditForm({
      first_name: guest.guest_first_name || guest.first_name || '',
      last_name: guest.guest_last_name || guest.last_name || '',
      guest_name: guest.guest_name || '',
      phone: guest.guest_phone || guest.phone || guest.phone_number || '',
      email: guest.guest_email || guest.email || '',
      expires_at: guest.expires_at || '',
    });
    setModalOpen(true);

    // Fetch audit events for this guest
    setAuditLoading(true);
    try {
      const events = await apiList('access_events');
      const guestEvents = (events || [])
        .filter((e: any) => e.guest_pin_id === guest.id || e.pin_code === (guest.pin_code || guest.pin))
        .sort((a: any, b: any) => new Date(b.created_at || b._created_at).getTime() - new Date(a.created_at || a._created_at).getTime())
        .slice(0, 50);
      setAuditEvents(guestEvents);
    } catch {
      setAuditEvents([]);
    } finally {
      setAuditLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedGuest(null);
    setModalDoorAccess([]);
  };

  // Property devices for the selected guest
  const propertyDevices = useMemo(() => {
    if (!selectedGuest) return [];
    return eligibleUnlockDevices(devices.filter(d => String(d.property_id) === String(selectedGuest.property_id)));
  }, [selectedGuest, devices]);

  const toggleDoor = (deviceId: string) => {
    setModalDoorAccess(prev =>
      prev.includes(deviceId)
        ? prev.filter(id => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  const handleSaveDoors = async () => {
    if (!selectedGuest) return;
    setSavingDoors(true);
    try {
      if (selectedGuest._source === 'visitor_pass') {
        await apiPost(`/visitors/pass/${selectedGuest.public_id}/access-scope`, {
          access_scope_mode: modalDoorAccess.includes('*') ? 'all' : modalDoorAccess.length ? 'selected' : 'none',
          access_scope: modalDoorAccess,
        });
      } else {
        await apiUpdate('guest_pins', selectedGuest.uuid || selectedGuest.id, {
          door_access_json: JSON.stringify(modalDoorAccess),
        });
      }
      refreshAllCounters();
      setGuests(prev => prev.map(g =>
        g.id === selectedGuest.id ? { ...g, door_access_json: JSON.stringify(modalDoorAccess) } : g
      ));
      closeModal();
    } catch (e) {
      console.error('Failed to save door access:', e);
      alert(`Door access was not saved: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSavingDoors(false);
    }
  };

  // Immediate-save toggle — matches the intercom unit-visibility UX where each
  // click persists the change without a separate Save button.
  const setDoorAccessAndSave = async (nextIds: string[]) => {
    if (!selectedGuest) return;
    setSavingDoors(true);
    setModalDoorAccess(nextIds);
    try {
      if (selectedGuest._source === 'visitor_pass') {
        await apiPost(`/visitors/pass/${selectedGuest.public_id}/access-scope`, {
          access_scope_mode: nextIds.includes('*') ? 'all' : nextIds.length ? 'selected' : 'none',
          access_scope: nextIds,
        });
      } else {
        await apiUpdate('guest_pins', selectedGuest.uuid || selectedGuest.id, {
          door_access_json: JSON.stringify(nextIds),
        });
      }
      refreshAllCounters();
      setGuests(prev => prev.map(g =>
        g.id === selectedGuest.id ? { ...g, door_access_json: JSON.stringify(nextIds) } : g
      ));
    } catch (e) {
      console.error('Failed to update door access:', e);
      try {
        const saved = selectedGuest?.door_access_json;
        setModalDoorAccess(Array.isArray(saved) ? saved.map(String) : JSON.parse(saved || '[]').map(String));
      } catch {
        setModalDoorAccess([]);
      }
      alert(`Door access was not saved: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSavingDoors(false);
    }
  };

  const handleRevoke = async () => {
    if (!selectedGuest) return;
    const alreadyRevoked = selectedGuest._status === 'revoked';
    setRevoking(true);
    try {
      const update = alreadyRevoked
        ? { revoked: false, is_revoked: false }
        : { revoked: true, is_revoked: true };
      if (selectedGuest._source === 'visitor_pass') {
        if (alreadyRevoked) throw new Error('Revoked Visitor Passes cannot be reactivated; create a new pass instead.');
        await apiPost(`/visitors/pass/${selectedGuest.public_id}/revoke`, {});
      } else {
        await apiUpdate('guest_pins', selectedGuest.uuid || selectedGuest.id, update);
      }
      refreshAllCounters();
      setGuests(prev => prev.map(g =>
        g.id === selectedGuest.id ? { ...g, ...update } : g
      ));
      closeModal();
    } catch (e) {
      console.error('Failed to update guest status:', e);
    } finally {
      setRevoking(false);
    }
  };

  const handleCreateGuest = async () => {
    if (!newGuest.property_id || !newGuest.unit_id || !newGuest.name.trim() || !newGuest.phone.trim()) return;
    setCreating(true);
    try {
      const validFrom = newGuest.starts_at ? new Date(newGuest.starts_at) : new Date();
      const validUntil = newGuest.expires_at ? new Date(newGuest.expires_at) : new Date(validFrom.getTime() + 24 * 60 * 60 * 1000);
      const result = await apiPost('/visitors/passes', buildVisitorPassPayload({
        propertyId: newGuest.property_id,
        unitId: newGuest.unit_id,
        visitorName: newGuest.name,
        phone: newGuest.phone,
        email: newGuest.email,
        validFrom,
        validUntil,
        passType: 'visitor',
        deviceIds: newGuest.device_ids,
        accessMode: newGuest.access_mode,
        consentForm: newGuest.consent_enabled ? {
          enabled: true,
          title: newGuest.consent_title.trim(),
          text: newGuest.consent_text.trim(),
          acknowledgement: newGuest.consent_acknowledgement.trim(),
        } : undefined,
      }));
      setCreatedPass(result);
      await load();
      refreshAllCounters();
    } finally {
      setCreating(false);
    }
  };

  const toggleRowPin = async (guest: any) => {
    const id = String(guest.id);
    if (revealedPins[id]) {
      setRevealedPins((current) => { const next = { ...current }; delete next[id]; return next; });
      return;
    }
    setRevealingPinId(id);
    try {
      const pin = guest._source === 'visitor_pass'
        ? (await apiRequestGet(`/visitors/passes/${encodeURIComponent(id)}/pin`))?.pin_code
        : (guest.pin_code || guest.pin);
      if (pin) {
        setRevealedPins((current) => ({ ...current, [id]: String(pin) }));
        window.setTimeout(() => setRevealedPins((current) => {
          const next = { ...current }; delete next[id]; return next;
        }), 10000);
      }
    } finally {
      setRevealingPinId(null);
    }
  };

  const getStatusBadge = (status: string) => {
    if (status === 'active') return <Badge status="active">Active</Badge>;
    if (status === 'expired') return <Badge status="pending">Expired</Badge>;
    if (status === 'revoked') return <Badge status="suspended">Revoked</Badge>;
    return <Badge>{status}</Badge>;
  };

  const columns = [
    {
      key: 'guest_name',
      header: 'Guest Name',
      render: (g: any) => (
        <button
          onClick={() => openGuestModal(g)}
          className="font-medium text-brand-600 hover:text-brand-800 text-left"
        >
          {g._guest_name}
        </button>
      ),
    },
    {
      key: 'contact',
      header: 'Phone / Email',
      render: (g: any) => (
        <div className="text-sm">
          <div className="text-gray-700">{g._phone}</div>
          {g._email !== '—' && <div className="text-gray-400 text-xs">{g._email}</div>}
        </div>
      ),
    },
    {
      key: 'property',
      header: 'Property',
      render: (g: any) => <span className="font-medium text-gray-900">{g._property_name}</span>,
    },
    {
      key: 'granted_by',
      header: 'Granted By',
      render: (g: any) => g._granted_by_name,
    },
    {
      key: 'unit',
      header: 'Unit',
      render: (g: any) => g._unit !== '—' ? `#${g._unit}` : '—',
    },
    {
      key: 'pin',
      header: 'PIN Code',
      render: (g: any) => {
        const id = String(g.id);
        const revealed = revealedPins[id];
        return (
          <div className="inline-flex items-center gap-2" onClick={(event) => event.stopPropagation()}>
            <span className="font-mono text-gray-700 bg-gray-100 px-2 py-0.5 rounded">{revealed || '••••••'}</span>
            <button type="button" className="row-open" disabled={revealingPinId === id} onClick={() => toggleRowPin(g)}>
              {revealingPinId === id ? 'Loading…' : revealed ? 'Hide' : 'View'}
            </button>
          </div>
        );
      },
    },
    {
      key: 'access_start',
      header: 'Access Start',
      render: (g: any) => formatDate(g.created_at || g._created_at),
    },
    {
      key: 'access_end',
      header: 'Access End',
      render: (g: any) =>
        g.expires_at ? formatDate(g.expires_at) : <span className="text-gray-400 text-xs">No Expiry</span>,
    },
    {
      key: 'doors',
      header: 'Doors',
      render: (g: any) => {
        const propDevices = eligibleUnlockDevices(devices.filter(d => String(d.property_id) === String(g.property_id)));
        return (
          <span className="text-sm text-gray-600">
            {formatDoorAccess(g.door_access_json, propDevices)}
          </span>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (g: any) => getStatusBadge(g._status),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (g: any) => (
        <button
          onClick={() => openGuestModal(g)}
          className="row-open"
        >
          Open <span aria-hidden="true">→</span>
        </button>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  return (
    <div className="shadow-list-page prototype-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Guests</h1>
          <p className="text-gray-500 mt-1">Guest PIN access across all properties</p>
        </div>
      </div>
      {loadWarning && <div role="alert" className="mb-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800">{loadWarning}</div>}

      <div className="prototype-toolbar prototype-toolbar-pill-row">
        <SearchInput value={search} onChange={setSearch} placeholder="Search guests..." />
        <FilterPills ariaLabel="Filter guests by status" value={statusFilter} onChange={(value) => setStatusFilter(value as StatusFilter)} options={[{value:'all',label:'All',count:totalGuests},{value:'active',label:'Active',count:activeCount},{value:'expired',label:'Expired',count:expiredCount},{value:'revoked',label:'Revoked',count:revokedCount}]}/>
        <button className="prototype-refresh" onClick={load} aria-label="Refresh guests">
          <RefreshCw /> <span>Refresh</span>
        </button>
        <Button onClick={() => setShowCreate(true)}>Add Guest</Button>
      </div>

      <CountBand value={filtered.length} detail="Guests in the current accessible portfolio" />

      {/* Summary Cards */}
      <div className="legacy-summary-cards grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Guests</p>
              <p className="text-2xl font-bold text-gray-900">{totalGuests}</p>
            </div>
            <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-brand-600" />
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Active</p>
              <p className="text-2xl font-bold text-green-600">{activeCount}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <UserCheck className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Expired</p>
              <p className="text-2xl font-bold text-yellow-600">{expiredCount}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Revoked</p>
              <p className="text-2xl font-bold text-red-600">{revokedCount}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <UserX className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden mb-4 space-y-3">
        <div className="grid grid-cols-1 gap-2">
          {filtered.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-sm text-gray-400">No guests found.</div>
          ) : pageData.map((g: any) => {
            const status = (g.status || 'active').toLowerCase();
            const sc = status === 'active' ? 'bg-green-100 text-green-700' : status === 'expired' ? 'bg-gray-100 text-gray-500' : 'bg-amber-100 text-amber-700';
            return (
              <div key={g.id} className="bg-white rounded-xl border border-gray-200 p-3">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{[g.guest_first_name, g.guest_last_name].filter(Boolean).join(' ') || g.guest_name || '—'}</p>
                    <p className="text-xs text-gray-500 truncate">📍 {g._property_name} · Unit {g._unit}</p>
                    <p className="text-xs text-gray-600 truncate">Granted by {g._granted_by_name}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${sc}`}>{status}</span>
                </div>
                {g.pin && <p className="text-xs font-mono text-gray-500">PIN {g.pin}</p>}
                <div className="mobile-card-actions">
                  <button onClick={() => openGuestModal(g)} className="row-open">Manage <span aria-hidden="true">→</span></button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Desktop table */}
      <Card padding={false} className="hidden md:block">
        <div className="legacy-table-controls p-4 border-b border-gray-100">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[240px]">
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Search by name, phone, property..."
              />
            </div>
            <div className="flex items-center gap-2">
              {(['all', 'active', 'expired', 'revoked'] as StatusFilter[]).map((f) => (
                <button
                  key={f}
                  onClick={() => setStatusFilter(f)}
                  className={`px-3 py-2 text-sm rounded-lg capitalize ${
                    statusFilter === f
                      ? 'bg-brand-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <Table
          columns={columns}
          data={pageData}
          emptyMessage="No guests found"
        />

        <Pagination
          page={page}
          totalPages={totalPages}
          totalItems={filtered.length}
          startIndex={startIndex}
          endIndex={endIndex}
          onPageChange={setPage}
        />
      </Card>

      <Modal open={showCreate} onClose={() => { setShowCreate(false); setCreatedPass(null); }} title="Add Visitor">
        <div className="space-y-4">
          {createdPass ? <div className="border border-emerald-300 bg-emerald-50 p-4 space-y-3">
            <p className="font-semibold text-gray-900">Visitor pass created</p>
            <p className="text-sm text-gray-600">Send this secure link to the visitor. The visitor can recover access by text message using the phone number entered below.</p>
            {createdPass.directory_code && <div><p className="text-xs font-bold uppercase tracking-wider text-gray-500">Directory code</p><p className="font-mono text-2xl font-bold tracking-widest text-gray-900">{createdPass.directory_code}</p><p className="text-xs text-gray-600">The visitor can enter this at the property directory. Your intercom setting controls whether it grants entry only or also opens their Visitor Pass.</p></div>}
            <a className="block text-sm font-semibold text-emerald-700 underline break-all" href={createdPass.pass_url} target="_blank" rel="noreferrer">Open visitor pass</a>
            <div className="flex gap-2"><Button onClick={() => navigator.clipboard.writeText(createdPass.pass_url)}>Copy secure link</Button><Button variant="secondary" onClick={() => { setCreatedPass(null); setNewGuest(emptyGuest); }}>Add another</Button></div>
          </div> : <>
          <label className="block text-sm font-medium text-gray-700">
            Property
            <select className="mt-1 w-full border border-gray-300 px-3 py-2" value={newGuest.property_id} onChange={(e) => setNewGuest((v) => ({ ...v, property_id: e.target.value, unit_id: '' }))}>
              <option value="">Select a property</option>
              {Object.values(properties).sort((a: any, b: any) => String(a.name).localeCompare(String(b.name))).map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
          <label className="block text-sm font-medium text-gray-700">
            Unit
            <select className="mt-1 w-full border border-gray-300 px-3 py-2" value={newGuest.unit_id} onChange={(e) => setNewGuest((v) => ({ ...v, unit_id: e.target.value }))}>
              <option value="">Select a unit</option>
              {units
                .filter((unit: any) => String(unit.property_id) === String(newGuest.property_id))
                .sort((a: any, b: any) => String(a.unit_number || '').localeCompare(String(b.unit_number || ''), undefined, { numeric: true }))
                .map((unit: any) => {
                  const property = properties[String(unit.property_id)];
                  const residents = Object.values(tenants)
                    .filter((tenant: any) => String(tenant.unit_id) === String(unit.id))
                    .map((tenant: any) => [tenant.first_name, tenant.last_name].filter(Boolean).join(' '))
                    .filter(Boolean)
                    .join(', ');
                  return <option key={unit.id} value={unit.id}>{property?.name || 'Property'} · Unit {unit.unit_number || unit.display_name || 'Unnumbered'}{residents ? ` · ${residents}` : ''}</option>;
                })}
            </select>
          </label>
          <label className="block text-sm font-medium text-gray-700">
            Guest name
            <input className="mt-1 w-full border border-gray-300 px-3 py-2" value={newGuest.name} onChange={(e) => setNewGuest((v) => ({ ...v, name: e.target.value }))} />
          </label>
          <label className="block text-sm font-medium text-gray-700">
            Visitor phone
            <input className="mt-1 w-full border border-gray-300 px-3 py-2" type="tel" autoComplete="tel" value={newGuest.phone} onChange={(e) => setNewGuest((v) => ({ ...v, phone: e.target.value }))} />
          </label>
          <label className="block text-sm font-medium text-gray-700">Email (optional)<input className="mt-1 w-full border border-gray-300 px-3 py-2" type="email" value={newGuest.email} onChange={(e) => setNewGuest((v) => ({ ...v, email: e.target.value }))} /></label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3"><label className="block text-sm font-medium text-gray-700">Access starts<input className="mt-1 w-full border border-gray-300 px-3 py-2" type="datetime-local" value={newGuest.starts_at} onChange={(e) => setNewGuest((v) => ({ ...v, starts_at: e.target.value }))} /></label><label className="block text-sm font-medium text-gray-700">Access ends<input className="mt-1 w-full border border-gray-300 px-3 py-2" type="datetime-local" value={newGuest.expires_at} onChange={(e) => setNewGuest((v) => ({ ...v, expires_at: e.target.value }))} /></label></div>
          <fieldset className="border border-gray-300 p-3">
            <legend className="px-1 text-sm font-semibold text-gray-800">Authorized doors</legend>
            <div className="mb-3 flex flex-wrap gap-3 text-sm"><label><input type="radio" name="guest-access-mode" checked={newGuest.access_mode === 'all'} onChange={() => setNewGuest(v => ({ ...v, access_mode: 'all', device_ids: [] }))}/> All eligible doors</label><label><input type="radio" name="guest-access-mode" checked={newGuest.access_mode === 'selected'} onChange={() => setNewGuest(v => ({ ...v, access_mode: 'selected' }))}/> Selected doors</label><label><input type="radio" name="guest-access-mode" checked={newGuest.access_mode === 'none'} onChange={() => setNewGuest(v => ({ ...v, access_mode: 'none', device_ids: [] }))}/> No door access</label></div>
            {newGuest.access_mode === 'selected' && (eligibleUnlockDevices(devices.filter((d: any) => String(d.property_id) === String(newGuest.property_id))).length === 0
              ? <p className="text-sm text-gray-500">No assigned access devices for this property.</p>
              : <div className="space-y-2">{eligibleUnlockDevices(devices.filter((d: any) => String(d.property_id) === String(newGuest.property_id))).map((d: any) => {
                const id = deviceAccessId(d);
                return <label key={id} className="flex items-center gap-2 text-sm text-gray-800"><input type="checkbox" checked={newGuest.device_ids.includes(id)} onChange={() => setNewGuest(v => ({ ...v, device_ids: v.device_ids.includes(id) ? v.device_ids.filter(x => x !== id) : [...v.device_ids, id] }))}/><span className="font-medium">{d.name || 'Unnamed door'}</span>{d.manufacturer && <span className="text-xs text-gray-500">{d.manufacturer}</span>}</label>;
              })}</div>)}
          </fieldset>
          <p className="text-xs text-gray-600">Effective access: {newGuest.access_mode === 'all' ? 'All current and future eligible doors at this property' : newGuest.access_mode === 'none' ? 'No door unlock access' : `${newGuest.device_ids.length} selected door${newGuest.device_ids.length === 1 ? '' : 's'}`}. Ends {newGuest.expires_at ? new Date(newGuest.expires_at).toLocaleString() : '24 hours after access starts'}.</p>
          <fieldset className="border border-gray-300 p-3 space-y-3">
            <legend className="px-1 text-sm font-semibold text-gray-800">Visitor consent</legend>
            <label className="flex items-center gap-2 text-sm text-gray-800"><input type="checkbox" checked={newGuest.consent_enabled} onChange={(e) => setNewGuest(v => ({ ...v, consent_enabled: e.target.checked }))}/> Require acceptance during onboarding</label>
            {newGuest.consent_enabled && <>
              <label className="block text-sm font-medium text-gray-700">Form title<input className="mt-1 w-full border border-gray-300 px-3 py-2" value={newGuest.consent_title} onChange={(e) => setNewGuest(v => ({ ...v, consent_title: e.target.value }))}/></label>
              <label className="block text-sm font-medium text-gray-700">Consent disclosure<textarea className="mt-1 min-h-32 w-full border border-gray-300 px-3 py-2" value={newGuest.consent_text} onChange={(e) => setNewGuest(v => ({ ...v, consent_text: e.target.value }))}/></label>
              <label className="block text-sm font-medium text-gray-700">Acknowledgement label<input className="mt-1 w-full border border-gray-300 px-3 py-2" value={newGuest.consent_acknowledgement} onChange={(e) => setNewGuest(v => ({ ...v, consent_acknowledgement: e.target.value }))}/></label>
            </>}
          </fieldset>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button disabled={creating || !newGuest.property_id || !newGuest.unit_id || !newGuest.name.trim() || !newGuest.phone.trim() || (newGuest.access_mode === 'selected' && newGuest.device_ids.length === 0) || (newGuest.consent_enabled && !newGuest.consent_text.trim())} onClick={handleCreateGuest}>{creating ? 'Creating…' : 'Create visitor pass'}</Button>
          </div>
          </>}
        </div>
      </Modal>

      {/* Guest Detail Modal */}
      {selectedGuest && (
        <Modal
          open={modalOpen}
          onClose={closeModal}
          title={`Guest: ${selectedGuest._guest_name}`}
          size="lg"
        >
          <div className="space-y-5">
            {/* Guest details */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Phone</p>
                <p className="font-medium text-gray-900">{selectedGuest._phone}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Email</p>
                <p className="font-medium text-gray-900">{selectedGuest._email}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Property</p>
                <p className="font-medium text-gray-900">{selectedGuest._property_name}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Granted By</p>
                <p className="font-medium text-gray-900">{selectedGuest._granted_by_name}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">PIN Code</p>
                <div className="flex items-center gap-2">
                  <p className="font-mono font-bold text-gray-900">
                    {pinRevealed ? (selectedGuest.pin_code || selectedGuest.pin || '—') : '••••'}
                  </p>
                  <button
                    onClick={() => setPinRevealed(!pinRevealed)}
                    className="text-xs text-brand-600 hover:underline"
                  >
                    {pinRevealed ? 'Hide' : 'View PIN'}
                  </button>
                  {pinRevealed && (
                    <button
                      onClick={() => { navigator.clipboard.writeText(selectedGuest.pin_code || selectedGuest.pin || ''); }}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      Copy
                    </button>
                  )}
                </div>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Status</p>
                {getStatusBadge(selectedGuest._status)}
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Access Start</p>
                <p className="font-medium text-gray-900">
                  {formatDateTimeWithZone(selectedGuest.valid_from || selectedGuest.starts_at || selectedGuest.created_at || selectedGuest._created_at)}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Access End</p>
                <p className="font-medium text-gray-900">
                  {selectedGuest.expires_at ? formatDateTimeWithZone(selectedGuest.expires_at) : 'No Expiry'}
                </p>
              </div>
            </div>

            {/* Edit Guest Info */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-gray-900">Guest Info</p>
                <button
                  onClick={() => setEditingGuest(!editingGuest)}
                  className="text-xs text-brand-600 hover:underline"
                >
                  {editingGuest ? 'Cancel' : 'Edit'}
                </button>
              </div>
              {editingGuest && (
                <div className="space-y-2 p-3 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-gray-500">First Name</label>
                      <input type="text" value={guestEditForm.first_name} onChange={e => setGuestEditForm((f: any) => ({ ...f, first_name: e.target.value }))} className="w-full px-2 py-1 text-sm border border-gray-300 rounded" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Last Name</label>
                      <input type="text" value={guestEditForm.last_name} onChange={e => setGuestEditForm((f: any) => ({ ...f, last_name: e.target.value }))} className="w-full px-2 py-1 text-sm border border-gray-300 rounded" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Phone</label>
                      <input type="tel" value={guestEditForm.phone} onChange={e => setGuestEditForm((f: any) => ({ ...f, phone: e.target.value }))} className="w-full px-2 py-1 text-sm border border-gray-300 rounded" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Email</label>
                      <input type="email" value={guestEditForm.email} onChange={e => setGuestEditForm((f: any) => ({ ...f, email: e.target.value }))} className="w-full px-2 py-1 text-sm border border-gray-300 rounded" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Access Expires ({localTimeZone()})</label>
                      <input type="datetime-local" value={toLocalDateTimeInput(guestEditForm.expires_at)} onChange={e => setGuestEditForm((f: any) => ({ ...f, expires_at: localDateTimeInputToIso(e.target.value) }))} className="w-full px-2 py-1 text-sm border border-gray-300 rounded" />
                    </div>
                  </div>
                  <button
                    className="px-3 py-1.5 text-xs font-medium rounded bg-brand-600 text-white hover:bg-brand-700"
                    onClick={async () => {
                      if (!selectedGuest) return;
                      try {
                        await apiUpdate('guest_pins', selectedGuest.uuid || selectedGuest.id, {
                          guest_first_name: guestEditForm.first_name,
                          guest_last_name: guestEditForm.last_name,
                          guest_name: [guestEditForm.first_name, guestEditForm.last_name].filter(Boolean).join(' '),
                          guest_phone: guestEditForm.phone,
                          guest_email: guestEditForm.email,
                          expires_at: guestEditForm.expires_at,
                        });
                        refreshAllCounters();
                        setGuests(prev => prev.map(g => g.id === selectedGuest.id ? { ...g, ...guestEditForm, guest_name: [guestEditForm.first_name, guestEditForm.last_name].filter(Boolean).join(' ') } : g));
                        setEditingGuest(false);
                      } catch (e) {
                        console.error('Failed to update guest:', e);
                      }
                    }}
                  >
                    Save Changes
                  </button>
                </div>
              )}
            </div>

            {/* Access Audit Trail */}
            <div>
              <p className="text-sm font-semibold text-gray-900 mb-2">Access Audit Trail</p>
              {auditLoading ? (
                <p className="text-xs text-gray-400">Loading events...</p>
              ) : auditEvents.length === 0 ? (
                <p className="text-xs text-gray-400">No access events recorded</p>
              ) : (
                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                  {auditEvents.map((event: any, idx: number) => (
                    <div key={idx} className="px-3 py-2 text-xs flex items-center justify-between">
                      <div>
                        <span className={`font-medium ${event.event_type === 'unlock' || event.action === 'unlock' ? 'text-green-600' : 'text-gray-700'}`}>
                          {event.event_type || event.action || 'Access'}
                        </span>
                        {event.door_name && <span className="text-gray-400 ml-2">— {event.door_name}</span>}
                      </div>
                      <span className="text-gray-400">
                        {event.created_at ? new Date(event.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Door Access */}
            <div>
              {(() => {
                const isActive = selectedGuest._status === 'active';
                const allIds = propertyDevices.map((d: any) => deviceAccessId(d));
                const legacyEmptyMeansAll = selectedGuest._source !== 'visitor_pass';
                const allMode = modeFromScope(modalDoorAccess, legacyEmptyMeansAll) === 'all';
                const accessibleSet = new Set(effectiveAccessIds(modalDoorAccess, allIds, legacyEmptyMeansAll));
                const accessibleCount = accessibleSet.size;
                return (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-semibold text-gray-900">Door Access</p>
                      {propertyDevices.length > 0 && (
                        <span className="text-xs text-gray-500">
                          {accessibleCount}/{propertyDevices.length} accessible
                        </span>
                      )}
                    </div>
                    {selectedGuest._source === 'visitor_pass' && isActive && <div className="mb-2 flex flex-wrap gap-2 text-xs">
                      <button type="button" disabled={savingDoors} onClick={() => setDoorAccessAndSave(['*'])} className="rounded-full border border-gray-300 px-3 py-1.5">All eligible doors</button>
                      <button type="button" disabled={savingDoors} onClick={() => setDoorAccessAndSave([])} className="rounded-full border border-gray-300 px-3 py-1.5">No door access</button>
                      <span className="self-center text-gray-600">{allMode ? 'All current and future eligible doors' : accessibleCount ? `${accessibleCount} selected doors` : 'No doors'} </span>
                    </div>}
                    {propertyDevices.length === 0 ? (
                      <p className="text-sm text-gray-500 italic">
                        No doors configured on this property.
                      </p>
                    ) : !isActive ? (
                      <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                        {propertyDevices
                          .filter((d: any) => accessibleSet.has(String(d.id)))
                          .map((device: any) => (
                            <div key={device.id} className="px-3 py-2 flex items-center justify-between text-sm">
                              <span className="text-gray-700">{device.name || device.device_name || `Device #${device.id}`}</span>
                              {device.type && <span className="text-xs text-gray-400">{device.type}</span>}
                            </div>
                          ))}
                        {accessibleCount === 0 && (
                          <div className="px-3 py-2 text-xs text-gray-400 italic">No accessible doors.</div>
                        )}
                      </div>
                    ) : (
                      <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-64 overflow-y-auto">
                        {propertyDevices.map((device: any) => {
                          const deviceId = deviceAccessId(device);
                          const hasAccess = accessibleSet.has(deviceId);
                          return (
                            <div key={device.id} className="px-3 py-2 flex items-center gap-3">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {device.name || device.device_name || `Device #${device.id}`}
                                </p>
                                {device.type && (
                                  <p className="text-[11px] text-gray-500">{device.type}</p>
                                )}
                              </div>
                              <button
                                type="button"
                                disabled={savingDoors}
                                onClick={() => {
                                  // Materialize all-door mode into the current explicit IDs
                                  // before removing one door. Empty pass scope remains none.
                                  const base = allMode ? allIds : modalDoorAccess;
                                  const next = hasAccess
                                    ? base.filter((id: string) => id !== deviceId)
                                    : Array.from(new Set([...base, deviceId]));
                                  setDoorAccessAndSave(next);
                                }}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-semibold transition-colors disabled:opacity-50 ${
                                  hasAccess
                                    ? 'bg-green-50 text-green-700 hover:bg-red-50 hover:text-red-700 border border-green-200 hover:border-red-200'
                                    : 'bg-gray-100 text-gray-500 hover:bg-green-50 hover:text-green-700 border border-gray-200 hover:border-green-200'
                                }`}
                                title={hasAccess ? 'Click to remove access' : 'Click to grant access'}
                              >
                                <span className={`w-1.5 h-1.5 rounded-full ${hasAccess ? 'bg-green-500' : 'bg-gray-400'}`} />
                                {hasAccess ? 'Remove access' : 'Grant access'}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
              <button
                onClick={handleRevoke}
                disabled={revoking}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  selectedGuest._status === 'revoked'
                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                    : 'bg-red-100 text-red-700 hover:bg-red-200'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {revoking
                  ? 'Saving...'
                  : selectedGuest._status === 'revoked'
                  ? 'Reactivate Guest'
                  : 'Revoke Access'}
              </button>

              <div className="flex items-center gap-2">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveDoors}
                  disabled={savingDoors || propertyDevices.length === 0}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-brand-600 text-white hover:bg-brand-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingDoors ? 'Saving...' : 'Save Door Access'}
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
